import AutorenewIcon from '@mui/icons-material/Autorenew'
import { IconButton, Tooltip } from '@mui/material'
import { useState } from 'react'
import styled, { css } from 'styled-components'

import { FormlessText } from '@/components/UberForm/Input/Text/Text'
import { useAppContext } from '@/hooks'
import { useAdjustTableNameUsingPostMutation } from '@/rtkApi'
import { FormValue } from '@/types'
import { logError } from '@/utils/errorHandling'

import { FormFieldContext } from '../UberForm/FormFieldContext'

type CodeInputFormlessProps = FormFieldContext & {
	autoFocus?: boolean
	error?: string | null
	hasNoBorder?: boolean
	isUpperCase?: boolean
	nameValue?: string
	onBlurCustom?: () => void
	onFocusCustom?: () => void
	readonly?: boolean
	readonlyContainer?: (value?: string) => React.ReactNode
	showGenerateButton?: boolean
	value?: string
}

export const CodeInputFormless = ({
	name,
	value,
	onChange,
	error,
	onFocus,
	onBlur,
	onFocusCustom,
	onBlurCustom,
	autoFocus,
	readonly,
	readonlyContainer,
	disabled,
	hasNoBorder,
	showGenerateButton = false,
	nameValue,
	isUpperCase = true,
}: CodeInputFormlessProps) => {
	const { t } = useAppContext()
	const [adjustTableNameMutation] = useAdjustTableNameUsingPostMutation()
	const [isGenerating, setIsGenerating] = useState(false)

	const handleChange = (value: FormValue) => {
		onChange && onChange(value)
	}

	const onFocusHandler = (e: any) => {
		if (onFocus) {
			onFocus(e)
		}

		if (onFocusCustom) {
			onFocusCustom()
		}
	}

	const onBlurHandler = (e: any) => {
		if (onBlur) {
			onBlur(e)
		}

		if (onBlurCustom) {
			onBlurCustom()
		}
	}

	const handleGenerateCode = async () => {
		if (!nameValue || disabled) return

		setIsGenerating(true)
		try {
			const response = await adjustTableNameMutation({
				utilsNameRequestDto: { name: nameValue },
			}).unwrap()

			const generatedCode = isUpperCase
				? response.result
				: response.result.toLowerCase()

			onChange?.(generatedCode)
		} catch (error) {
			logError(error, { operation: 'CodeInputFormless.handleGenerateCode' })
		} finally {
			setIsGenerating(false)
		}
	}

	const renderReadonly = readonlyContainer ? (
		readonlyContainer(value)
	) : (
		<FormlessTextStyled value={value} disabled />
	)

	const canGenerate = showGenerateButton && !disabled && !readonly

	const renderInteractive = (
		<InputContainer>
			<FormlessTextStyled
				name={name}
				value={value}
				onFocus={onFocusHandler}
				onBlur={onBlurHandler}
				error={error}
				onChange={handleChange}
				autoFocus={autoFocus}
				disabled={disabled}
				hasNoBorder={hasNoBorder}
			/>
			{canGenerate && (
				<GenerateButtonWrapper>
					<Tooltip title={t('GENERATE_CODE_FROM_NAME')}>
						<span>
							<IconButton
								size="small"
								onClick={handleGenerateCode}
								disabled={!nameValue || isGenerating}
								data-testid={`code-input-button-generate-${name}`}
							>
								<AutorenewIcon
									fontSize="small"
									className={isGenerating ? 'animate-spin' : ''}
								/>
							</IconButton>
						</span>
					</Tooltip>
				</GenerateButtonWrapper>
			)}
		</InputContainer>
	)

	return readonly ? renderReadonly : renderInteractive
}

const InputContainer = styled.div`
	display: flex;
	align-items: center;
	width: 100%;
`

const GenerateButtonWrapper = styled.div`
	margin-left: -32px;
	z-index: 1;
`

const FormlessTextStyled = styled(FormlessText)<
	React.ComponentProps<
		typeof FormlessText & { disabled: boolean; error?: string }
	>
>`
	width: 100%;
	background: ${(props) =>
		props.disabled ? props.theme.colors.input.disabled.background : ''};

	${(props) =>
		props.error &&
		css`
			background: ${props.theme.colors.input.error.background};
			border-color: ${props.theme.colors.input.error.border};
		`}
`
